import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Search, Play, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { EntityRecordsSyncPanel } from '../components/admin/EntityRecordsSyncPanel';
import { CoverageMatrixPanel } from '../components/admin/CoverageMatrixPanel';
import { EntityMergePanel } from '../components/admin/EntityMergePanel';
import { diagnosticsAPI } from '../lib/api';
import { useEntityStore } from '../store';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

const FEED_STATUS_BADGE = {
  live:   { variant: 'default', className: 'bg-green-100 text-green-800 border-green-300' },
  stale:  { variant: 'default', className: 'bg-amber-100 text-amber-800 border-amber-300' },
  manual: { variant: 'default', className: 'bg-blue-100 text-blue-800 border-blue-300' },
  none:   { variant: 'outline', className: 'text-gray-500' },
};

function BankAccountsTab() {
  const [loading, setLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [inventory, setInventory] = useState(null);
  const entities = useEntityStore((s) => s.entities);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.adminBankAccountInventory();
      setInventory(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  const runBackfillPreview = async () => {
    setBackfillLoading(true);
    setBackfillPreview(null);
    try {
      const { data } = await diagnosticsAPI.bankAccountBackfillPreview();
      setBackfillPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBackfillLoading(false);
    }
  };

  const runBackfillApply = async () => {
    setBackfillLoading(true);
    try {
      const { data } = await diagnosticsAPI.bankAccountBackfillApply();
      toast.success(`Backfill applied: ${data.updated_count} accounts updated`);
      setBackfillPreview(null);
      await loadInventory();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleAssign = async (accountId, entityUuid) => {
    try {
      await diagnosticsAPI.bankAccountAssign(accountId, { entity_uuid: entityUuid || '' });
      toast.success('Account assigned');
      await loadInventory();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleMarkManual = async (accountId) => {
    try {
      await diagnosticsAPI.bankAccountAssign(accountId, { feed_status: 'manual' });
      toast.success('Marked as manual');
      await loadInventory();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const renderAccountRow = (acc) => {
    const feedBadge = FEED_STATUS_BADGE[acc.feed_status] || FEED_STATUS_BADGE.none;
    return (
      <tr key={acc.id} className="border-b last:border-0 hover:bg-muted/20">
        <td className="px-2 py-1.5 text-sm">{acc.account_name}</td>
        <td className="px-2 py-1.5 text-sm">{acc.bank_name}</td>
        <td className="px-2 py-1.5 text-xs font-mono">{acc.currency}</td>
        <td className="px-2 py-1.5 text-xs">{acc.source || 'unknown'}</td>
        <td className="px-2 py-1.5">
          <Badge className={`text-[10px] ${feedBadge.className}`}>
            {acc.feed_status || 'none'}
          </Badge>
        </td>
        <td className="px-2 py-1.5 text-xs text-muted-foreground">
          {acc.last_synced ? new Date(acc.last_synced).toLocaleDateString() : 'never'}
        </td>
        <td className="px-2 py-1.5">
          <Select
            value={acc.entity_uuid || acc.suggested_entity_uuid || '__unassigned__'}
            onValueChange={(val) => handleAssign(acc.id, val === '__unassigned__' ? '' : val)}
          >
            <SelectTrigger className={`h-7 text-xs w-[180px] ${acc.suggested_entity_name && !acc.entity_uuid ? 'border-blue-400' : ''}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__unassigned__">
                <span className="text-muted-foreground">Unassigned</span>
              </SelectItem>
              {entities.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}{e.id === acc.suggested_entity_uuid && !acc.entity_uuid ? ' (suggested)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {acc.suggested_entity_name && !acc.entity_uuid && (
            <span className="text-[10px] text-blue-500 ml-1">IBAN match</span>
          )}
        </td>
        <td className="px-2 py-1.5">
          {acc.feed_status !== 'manual' && (
            <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => handleMarkManual(acc.id)}>
              Manual
            </Button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      {/* Backfill controls */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Bank Account Backfill</CardTitle>
          <p className="text-xs text-muted-foreground">
            Backfill feed_status and entity_uuid on existing accounts from the registry.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button onClick={runBackfillPreview} disabled={backfillLoading} variant="outline" size="sm">
              {backfillLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              Preview Backfill
            </Button>
            {backfillPreview?.change_count > 0 && (
              <Button onClick={runBackfillApply} disabled={backfillLoading} size="sm">
                {backfillLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                Apply ({backfillPreview.change_count} changes)
              </Button>
            )}
          </div>
          {backfillPreview && backfillPreview.change_count === 0 && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle2 className="h-4 w-4" /> All accounts already backfilled.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inventory table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Account Inventory</CardTitle>
            <Button onClick={loadInventory} disabled={loading} variant="ghost" size="sm">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {inventory && (
            <div className="space-y-4">
              <div className="flex gap-3 text-sm">
                <span>Total: <strong>{inventory.total}</strong></span>
                <span>Assigned: <strong>{inventory.assigned_count}</strong></span>
                {inventory.unassigned_count > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {inventory.unassigned_count} unassigned
                  </Badge>
                )}
              </div>

              {/* Unassigned accounts (highlighted) */}
              {inventory.unassigned?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-600 flex items-center gap-1 mb-1">
                    <AlertTriangle className="h-4 w-4" /> Unassigned Accounts
                  </h4>
                  <div className="overflow-x-auto border border-red-200 rounded">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-red-50/50">
                          <th className="text-left px-2 py-1.5 font-medium">Account</th>
                          <th className="text-left px-2 py-1.5 font-medium">Bank</th>
                          <th className="text-left px-2 py-1.5 font-medium">CCY</th>
                          <th className="text-left px-2 py-1.5 font-medium">Source</th>
                          <th className="text-left px-2 py-1.5 font-medium">Feed</th>
                          <th className="text-left px-2 py-1.5 font-medium">Last Sync</th>
                          <th className="text-left px-2 py-1.5 font-medium">Assign To</th>
                          <th className="text-left px-2 py-1.5 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventory.unassigned.map(renderAccountRow)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Assigned accounts */}
              {inventory.assigned?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Assigned Accounts</h4>
                  <div className="overflow-x-auto border rounded">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1.5 font-medium">Account</th>
                          <th className="text-left px-2 py-1.5 font-medium">Bank</th>
                          <th className="text-left px-2 py-1.5 font-medium">CCY</th>
                          <th className="text-left px-2 py-1.5 font-medium">Source</th>
                          <th className="text-left px-2 py-1.5 font-medium">Feed</th>
                          <th className="text-left px-2 py-1.5 font-medium">Last Sync</th>
                          <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                          <th className="text-left px-2 py-1.5 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventory.assigned.map(renderAccountRow)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function EntitiesAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'coverage';

  const handleTabChange = useCallback((value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'coverage') {
        next.delete('tab');
      } else {
        next.set('tab', value);
      }
      return next;
    });
  }, [setSearchParams]);

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 md:p-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Entities Admin</h1>
          <p className="text-muted-foreground mt-1">
            Registry sync, bank account assignment, and data source coverage.
          </p>
        </div>

        <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
          <TabsList>
            <TabsTrigger value="coverage">Coverage Matrix</TabsTrigger>
            <TabsTrigger value="entities">Entity Records</TabsTrigger>
            <TabsTrigger value="accounts">Bank Accounts</TabsTrigger>
          </TabsList>

          <TabsContent value="coverage" className="space-y-4 mt-4">
            <CoverageMatrixPanel />
          </TabsContent>

          <TabsContent value="entities" className="space-y-4 mt-4">
            <EntityRecordsSyncPanel />
            <EntityMergePanel />
          </TabsContent>

          <TabsContent value="accounts" className="space-y-4 mt-4">
            <BankAccountsTab />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
